import { useCallback, useState } from 'react';

function flattenNestedRows<T extends object>(data: T[]): T[] {
  if (!data || !Array.isArray(data)) return [];

  const result: T[] = [];
  const stack = [...data];

  while (stack.length > 0) {
    const item = stack.pop();
    if (item) result.push(item);
    const children = (item as Record<string, unknown>)?.children;
    if (children && Array.isArray(children) && children.length > 0) {
      stack.push(...(children as T[]));
    }
  }

  return result;
}

interface UseRowSelectionParams<T> {
  getItemId: (item: T) => string;
  totalItems: number;
}

export function useRowSelection<T extends object>({
  getItemId,
  totalItems,
}: UseRowSelectionParams<T>) {
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [unselectedItemIds, setUnselectedItemIds] = useState<Set<string>>(
    new Set()
  );
  const [isSelectAllActive, setIsSelectAllActive] = useState(false);

  const toggleChildrenSelection = useCallback(
    (item: T, isSelected: boolean) => {
      const record = item as Record<string, unknown>;
      if (
        !record.children ||
        !Array.isArray(record.children) ||
        record.children.length === 0
      ) {
        return;
      }

      const childrenFlattened = flattenNestedRows(record.children as T[]);

      if (isSelectAllActive) {
        setUnselectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) {
            childrenFlattened.forEach((child) => {
              next.delete(getItemId(child));
            });
          } else {
            childrenFlattened.forEach((child) => {
              next.add(getItemId(child));
            });
          }
          return next;
        });
      } else {
        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) {
            childrenFlattened.forEach((child) => {
              next.add(getItemId(child));
            });
          } else {
            childrenFlattened.forEach((child) => {
              next.delete(getItemId(child));
            });
          }
          return next;
        });
      }
    },
    [getItemId, isSelectAllActive]
  );

  const isItemSelected = useCallback(
    (item: T) => {
      const itemId = getItemId(item);
      if (isSelectAllActive) {
        return !unselectedItemIds.has(itemId);
      }
      return selectedItemIds.has(itemId);
    },
    [getItemId, isSelectAllActive, selectedItemIds, unselectedItemIds]
  );

  const handleSelectItem = useCallback(
    (item: T, isSelected: boolean) => {
      const itemId = getItemId(item);

      if (isSelectAllActive) {
        setUnselectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) next.delete(itemId);
          else next.add(itemId);
          return next;
        });
      } else {
        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) next.add(itemId);
          else next.delete(itemId);
          return next;
        });
      }

      const itemRecord = item as Record<string, unknown>;
      if (
        itemRecord.children &&
        Array.isArray(itemRecord.children) &&
        itemRecord.children.length > 0
      ) {
        toggleChildrenSelection(item, isSelected);
      }
    },
    [getItemId, isSelectAllActive, toggleChildrenSelection]
  );

  const handleSelectAll = useCallback((isSelected: boolean) => {
    if (isSelected) {
      setIsSelectAllActive(true);
      setUnselectedItemIds(new Set());
      setSelectedItemIds(new Set());
    } else {
      setIsSelectAllActive(false);
      setSelectedItemIds(new Set());
      setUnselectedItemIds(new Set());
    }
  }, []);

  const handleSelectPage = useCallback(
    (pageItems: T[], isSelected: boolean) => {
      const flattenedItems = flattenNestedRows(pageItems);
      const pageItemIds: string[] = flattenedItems.map(getItemId);

      if (isSelectAllActive) {
        setUnselectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) pageItemIds.forEach((id) => next.delete(id));
          else pageItemIds.forEach((id) => next.add(id));
          return next;
        });
      } else {
        setSelectedItemIds((prev) => {
          const next = new Set(prev);
          if (isSelected) pageItemIds.forEach((id) => next.add(id));
          else pageItemIds.forEach((id) => next.delete(id));
          return next;
        });
      }
    },
    [getItemId, isSelectAllActive]
  );

  const getSelectedItems = useCallback(
    (pageItems: T[]) => {
      const flattenedItems = flattenNestedRows(pageItems);
      return flattenedItems.filter((item) => isItemSelected(item));
    },
    [isItemSelected]
  );

  const getSelectionState = useCallback(() => {
    if (isSelectAllActive) {
      return {
        unselectedIds: Array.from(unselectedItemIds),
        totalSelected: totalItems - unselectedItemIds.size,
      };
    }
    return {
      selectedIds: Array.from(selectedItemIds),
      totalSelected: selectedItemIds.size,
    };
  }, [isSelectAllActive, selectedItemIds, totalItems, unselectedItemIds]);

  const resetSelection = useCallback(() => {
    setSelectedItemIds(new Set());
    setUnselectedItemIds(new Set());
    setIsSelectAllActive(false);
  }, []);

  return {
    selectedItemIds,
    unselectedItemIds,
    isSelectAllActive,
    isItemSelected,
    handleSelectItem,
    handleSelectPage,
    handleSelectAll,
    getSelectionState,
    getSelectedItems,
    resetSelection,
    totalSelected: isSelectAllActive
      ? totalItems - unselectedItemIds.size
      : selectedItemIds.size,
  };
}
